import { IItemPreviewProps } from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import commonStyle from '../common.module.scss'
import React, { useRef } from 'react'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import ProductActions from './ProductActions.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './ItemPreview.module.scss'
import Rating from '../components/Rating.tsx'
import { unescapeTags } from '@aytm/lib/common/func.js'
import { useBannerImpression } from '../../hooks/useBannerImpression'
import SponsoredLabel from './SponsoredLabel.tsx'
import ecoLeafIcon from '../img/ecoLeaf.png'
import infoCircleIcon from '../img/infoCircle.svg'

const ItemPreview = ({
  schema,
  width,
  questionSchema,
  onItemSelect,
  response,
  findability,
  onProductAction,
  disabled: _disabled,
  vertical,
  onSpin,
  className,
  saveScrollPoint,
  bannerPlacement,
  bannerSource,
  'data-testid': dataTestId
}: IItemPreviewProps) => {
  const randomInDate = useRef<Date>(null)
  const randomDate = useRef<Date>(null)
  const viewport = useViewport()
  const spinButtonRef = useRef<HTMLDivElement>(null)
  const { t, languageTag } = useSurveyLocale()

  // Banner impression tracking (source required only for product_page). Hook is
  // called unconditionally (React Rules of Hooks); a null `placement` short-circuits
  // inside the hook when bannerPlacement is absent.
  const bannerRef = useBannerImpression(schema.id, bannerPlacement ?? null, bannerSource)

  // Recalculate dates when product changes
  React.useEffect(() => {
    randomInDate.current = new Date()
    if (schema.prime) {
      randomInDate.current.setDate(randomInDate.current.getDate() + schema.prime - 1)
      randomDate.current = null
    } else {
      randomDate.current = new Date()
      randomDate.current.setDate(randomDate.current.getDate() + ((schema.text?.length || 0) % 2) + 1)
      randomInDate.current = new Date(randomDate.current)
      randomInDate.current.setDate(randomInDate.current.getDate() + ((schema.description?.length || 0) % 3) + 1)
    }
  }, [schema.id, schema.prime, schema.text?.length, schema.description?.length])

  const shouldAddPromoCompact = className?.includes(style.promo) && width < 300

  return (
    <div
      ref={bannerPlacement ? bannerRef : null}
      data-testid={dataTestId}
      className={`
        ${style.item}
        ${viewport.mobile ? style.Mobile : ''}
        ${viewport.mobile && 2 === questionSchema.products_per_row_mobile ? style.perRow2 : ''}
        ${className ?? ''}
        ${shouldAddPromoCompact ? style.promoCompact : ''}
      `}
      style={width && !viewport.mobile ? { width: width, maxWidth: width, minWidth: width } : null}
    >
      <div
        tabIndex={0}
        aria-label={t('questions', 'ec', 'amazon', 'productLabel')}
        className={`${style.preview}`}
        onClick={event => saveScrollPoint(event)}
        onKeyDown={event => {
          if (event.target instanceof HTMLButtonElement) return
          if ((' ' === event.key || 'Enter' === event.key) && schema.available) {
            event.preventDefault()
            onItemSelect()
          }
        }}
      >
        <div className={style.labels}>
          {schema.best_seller ? (
            <span className={commonStyle.bestSellerBadge}>{t('questions', 'ec', 'amazon', 'bestSeller')}</span>
          ) : (
            ''
          )}
          {schema.overal_pick && !schema.best_seller ? (
            <span className={commonStyle.overalPickBadge}>
              {t('questions', 'ec', 'amazon', 'overallPick')}
              <img src={infoCircleIcon} alt="" />
            </span>
          ) : (
            ''
          )}
        </div>
        <div
          className={style.image}
          onClick={schema.available ? onItemSelect : null}
          style={{ height: `${viewport.mobile ? '100%' : width}` }}
        >
          {viewport.mobile && <div className={style.imageIcon}></div>}
          {schema.image && schema.image.original_url ? (
            <img
              data-testid="SW-SH-ECommerceImage"
              className={style.ecommerceItemImage}
              alt={schema.image?.alt_text || t('questions', 'img', 'noAlt')}
              src={getMediumAssetPath(schema.image, 'transparent')}
            ></img>
          ) : (
            <div className={commonStyle.emptyImage} />
          )}
        </div>
        <div className={style.textBlock}>
          {schema.sponsored && <SponsoredLabel />}
          <span className={style.title} onClick={onItemSelect} dangerouslySetInnerHTML={{ __html: schema.text }}></span>
          <EmptyLink>
            <span className={`${commonStyle.stars} `}>
              <div className={commonStyle.numericRating}>{schema.rating}</div>
              <Rating size="small" rating={schema.rating || 0} />
              <span className={`${commonStyle.arrowDown} ${commonStyle.link}`}>
                {!viewport.mobile && <i />}
                <span>({schema.reviews?.toLocaleString() || ''})</span>
              </span>
            </span>
          </EmptyLink>
          {schema.bought && <span className={style.bought}>{unescapeTags(schema.bought)}</span>}
          {schema.red_label ? (
            <span className={commonStyle.redLabel} dangerouslySetInnerHTML={{ __html: schema.red_label }} />
          ) : (
            ''
          )}
          <div className={style.price}>
            <span
              className={style.bigPrice}
              onClick={onItemSelect}
              dangerouslySetInnerHTML={{
                __html: formatPriceWithLocale(
                  schema.discounted_price ? schema.discounted_price : schema.price,
                  questionSchema.price_mask,
                  languageTag,
                  true,
                  true
                )
              }}
            ></span>
            {schema.per_count_price ? (
              <span className={style.smallPrice}>({unescapeTags(schema.per_count_price)})</span>
            ) : (
              ''
            )}
            {schema.discounted_price && schema.price > schema.discounted_price ? (
              <span className={style.smallPrice}>
                {t('questions', 'ec', 'amazon', 'list')}&nbsp;
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(schema.price, questionSchema.price_mask, languageTag, true)
                  }}
                />
              </span>
            ) : (
              ''
            )}
          </div>

          {schema.subscribe_coupon_percent ? (
            <div
              className={style.subscribe}
              dangerouslySetInnerHTML={{
                __html: t<(percent: number) => string>(
                  'questions',
                  'ec',
                  'amazon',
                  'subscribeExtra'
                )(schema.subscribe_coupon_percent)
              }}
            />
          ) : schema.subscribe_percent ? (
            <div
              className={style.subscribe}
              dangerouslySetInnerHTML={{
                __html: t<(percent: number) => string>(
                  'questions',
                  'ec',
                  'amazon',
                  'subscribeSave'
                )(schema.subscribe_percent)
              }}
            />
          ) : (
            ''
          )}

          {schema.green_label ? (
            <span className={commonStyle.greenLabel} dangerouslySetInnerHTML={{ __html: schema.green_label }} />
          ) : (
            ''
          )}

          {schema.prime ? <i className={`${commonStyle['prime' + schema.prime]} ${commonStyle.primeLabel}`} /> : ''}
          {vertical ? (
            ''
          ) : (
            <div className={style.delivery}>
              {t('questions', 'ec', 'amazon', 'freeDelivery')}&nbsp;
              {1 == schema.prime ? 'today, ' : 2 == schema.prime ? 'tomorrow, ' : ''}
              <span>
                {randomInDate.current?.toLocaleDateString(languageTag, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </span>{' '}
              {!schema.prime && randomDate.current ? (
                <>
                  <br />
                  {t('questions', 'ec', 'amazon', 'fastDelivery')}&nbsp;
                  <span>
                    {randomDate.current.toLocaleDateString(languageTag, {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                </>
              ) : (
                ''
              )}
            </div>
          )}
          {schema.eco_label ? (
            <EmptyLink>
              <span className={`${commonStyle.ecoLabel} ${viewport.mobile ? commonStyle.ecoLabelMobile : ''}`}>
                <img src={ecoLeafIcon} width={18} height={18} alt="" />
                {viewport.mobile ? (
                  <span dangerouslySetInnerHTML={{ __html: schema.eco_label }} />
                ) : (
                  <span className={commonStyle.arrowDown}>
                    <span dangerouslySetInnerHTML={{ __html: schema.eco_label }} /> <i />
                  </span>
                )}
              </span>
            </EmptyLink>
          ) : (
            ''
          )}
          {vertical ? '' : <span className={style.bought}>{t('questions', 'ec', 'amazon', 'sold')}</span>}
          {(bannerPlacement == 'banner' || !bannerPlacement) && (
            <ProductActions
              ref={spinButtonRef}
              isMobile={viewport.mobile}
              response={response}
              findability={findability}
              index={schema.index}
              limit={questionSchema.max_qty_per_product}
              onProductAction={onProductAction}
              onSpin={onSpin}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ItemPreview
